/**
 * Onboarding Page
 *
 * Guides new users through profile setup, API key creation,
 * live testing, and code examples.
 *
 * @module pages/OnboardingPage
 */

import { useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '@/contexts/AuthContext';
import { functions } from '@/lib/firebase';
import { cn } from '@/lib/utils';
import { useOnboarding, usePageTitle } from '@/hooks';
import {
  OnboardingProgress,
  ProfileStep,
  ApiKeyStep,
  TestApiStep,
  CodeExamplesStep,
  ExploreMoreStep,
} from '@/components/onboarding';
import { PageSpinner } from '@/components/ui';

// =============================================================================
// Steps Configuration
// =============================================================================

const STEPS = [
  {
    number: 1,
    title: 'Profile',
    description: 'Tell us about yourself',
  },
  {
    number: 2,
    title: 'API Key',
    description: 'Your first API key',
  },
  {
    number: 3,
    title: 'Test API',
    description: 'Make your first call',
  },
  {
    number: 4,
    title: 'Code Examples',
    description: 'Start building',
  },
  {
    number: 5,
    title: 'Explore',
    description: 'Next steps',
  },
];

// =============================================================================
// Component
// =============================================================================

// Type for location state
interface LocationState {
  emailVerified?: boolean;
}

export function OnboardingPage() {
  usePageTitle('Get Started');

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { firebaseUser, user, organization, loading: authLoading } = useAuth();

  // Check if user just verified their email (via location state or query param)
  const locationState = location.state as LocationState | null;
  const verifiedFromState = locationState?.emailVerified === true;
  const verifiedFromQuery = searchParams.get('verified') === 'true';
  const justVerifiedEmail = verifiedFromState || verifiedFromQuery;

  const {
    currentStep,
    completedSteps,
    createdApiKey,
    testResult,
    loading,
    errors,
    saveProfileAndCreateKey,
    testApiKey,
    completeStep,
    goToStep,
    isStepAccessible,
    completeOnboarding,
  } = useOnboarding();

  // Refs for scroll-into-view
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);

  // ---------------------------------------------------------------------------
  // Redirect if onboarding already completed
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!authLoading && user?.onboardingCompletedAt) {
      navigate('/overview', { replace: true });
    }
  }, [authLoading, user?.onboardingCompletedAt, navigate]);

  // ---------------------------------------------------------------------------
  // Send welcome email after email verification
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (justVerifiedEmail && firebaseUser) {
      // Trigger welcome email (non-blocking, fire and forget)
      const sendWelcome = httpsCallable<void, { success: boolean }>(
        functions,
        'sendWelcomeEmailAfterVerification'
      );
      sendWelcome().catch((err) => {
        console.error('Failed to send welcome email:', err);
      });

      // Clear the state/query param so we don't send again on refresh
      // (but keep showing the success message for this session)
    }
  }, [justVerifiedEmail, firebaseUser]);

  // ---------------------------------------------------------------------------
  // Scroll to current step when it changes
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const stepRef = stepRefs.current[currentStep - 1];
    if (stepRef) {
      stepRef.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [currentStep]);

  // ---------------------------------------------------------------------------
  // Step Handlers
  // ---------------------------------------------------------------------------

  const handleProfileSubmit = useCallback(
    async (data: { firstName: string; lastName: string; organizationName?: string }) => {
      await saveProfileAndCreateKey(data);
    },
    [saveProfileAndCreateKey]
  );

  const handleApiKeyContinue = useCallback(() => {
    completeStep(2);
    goToStep(3);
  }, [completeStep, goToStep]);

  const handleTestContinue = useCallback(() => {
    // Step 3 is already completed by testApiKey on success
    // Now advance to step 4
    goToStep(4);
  }, [goToStep]);

  const handleCodeExamplesContinue = useCallback(() => {
    completeStep(4);
    goToStep(5);
  }, [completeStep, goToStep]);

  // ---------------------------------------------------------------------------
  // Loading State & Auth Check
  // ---------------------------------------------------------------------------

  // Still loading auth state
  if (authLoading) {
    return <PageSpinner />;
  }

  // Not logged in - redirect to login
  if (!firebaseUser) {
    navigate('/login', { replace: true });
    return <PageSpinner />;
  }

  // Waiting for user/org data to load
  if (!user || !organization) {
    return <PageSpinner />;
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-white">
      {/* Progress sidebar (desktop) / Header bar (mobile) */}
      <OnboardingProgress
        currentStep={currentStep}
        completedSteps={completedSteps}
        steps={STEPS}
        onStepClick={goToStep}
        isStepAccessible={isStepAccessible}
      />

      {/* Main content area */}
      <div className="flex-1 overflow-auto">
        {/* Steps container - large bottom padding ensures enough space to scroll steps to top */}
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:py-8 py-6 space-y-12 sm:space-y-16 pb-[50vh] lg:pb-[80vh]">
          {/* Step 1: Profile */}
          <div
            ref={(el) => {
              stepRefs.current[0] = el;
            }}
            className={cn(
              'scroll-mt-20 lg:scroll-mt-10',
              currentStep === 1 ? 'opacity-100' : 'opacity-50 pointer-events-none'
            )}
          >
            <ProfileStep
              onSubmit={handleProfileSubmit}
              loading={loading.profile || loading.apiKey}
              error={errors.profile || errors.apiKey}
              successMessage={justVerifiedEmail ? 'Email verified successfully!' : null}
              initialValues={{
                firstName: user.firstName || '',
                lastName: user.lastName || '',
              }}
            />
          </div>

          {/* Step 2: API Key */}
          {isStepAccessible(2) && createdApiKey && (
            <div
              ref={(el) => {
                stepRefs.current[1] = el;
              }}
              className={cn(
                'scroll-mt-20 lg:scroll-mt-10',
                currentStep === 2 ? 'opacity-100' : 'opacity-50 pointer-events-none'
              )}
            >
              <ApiKeyStep apiKey={createdApiKey} onContinue={handleApiKeyContinue} />
            </div>
          )}

          {/* Step 3: Test API */}
          {isStepAccessible(3) && createdApiKey && (
            <div
              ref={(el) => {
                stepRefs.current[2] = el;
              }}
              className={cn(
                'scroll-mt-20 lg:scroll-mt-10',
                currentStep === 3 ? 'opacity-100' : 'opacity-50 pointer-events-none'
              )}
            >
              <TestApiStep
                onTest={testApiKey}
                loading={loading.test}
                result={testResult}
                error={errors.test}
                onContinue={handleTestContinue}
              />
            </div>
          )}

          {/* Step 4: Code Examples */}
          {isStepAccessible(4) && createdApiKey && (
            <div
              ref={(el) => {
                stepRefs.current[3] = el;
              }}
              className={cn(
                'scroll-mt-20 lg:scroll-mt-10',
                currentStep === 4 ? 'opacity-100' : 'opacity-50 pointer-events-none'
              )}
            >
              <CodeExamplesStep
                apiKey={createdApiKey}
                onContinue={handleCodeExamplesContinue}
              />
            </div>
          )}

          {/* Step 5: Explore More */}
          {isStepAccessible(5) && (
            <div
              ref={(el) => {
                stepRefs.current[4] = el;
              }}
              className={cn(
                'scroll-mt-20 lg:scroll-mt-10',
                currentStep === 5 ? 'opacity-100' : 'opacity-50 pointer-events-none'
              )}
            >
              <ExploreMoreStep onComplete={completeOnboarding} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
